import { Amplify } from 'aws-amplify';
import {
  confirmResetPassword,
  confirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import { SITE_KEY_AUTH, captchaHeader } from './captcha';

export interface AppUser {
  id: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
  avatar?: string;
}

const domain = (import.meta.env.VITE_COGNITO_DOMAIN || '').replace(/^https?:\/\//, '');
const appUrl =
  import.meta.env.VITE_APP_URL || (typeof window !== 'undefined' ? window.location.origin : '');

// Initialize Amplify Cognito Auth
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
      loginWith: domain
        ? {
            oauth: {
              domain,
              scopes: ['openid', 'email', 'profile'],
              redirectSignIn: [appUrl],
              redirectSignOut: [appUrl],
              responseType: 'code',
            },
          }
        : undefined,
    },
  },
});

async function toAppUser(): Promise<AppUser> {
  const current = await getCurrentUser();
  const attrs = await fetchUserAttributes();
  return {
    id: current.userId,
    email: attrs.email || '',
    displayName: attrs.name || attrs.email || current.username,
    emailVerified: attrs.email_verified === 'true',
    avatar: attrs.picture,
  };
}

// Soulstack Verify gate for the Cognito calls a bot could farm: sign in, sign
// up, password-reset request and resend code. Each takes a fresh one-use token
// from the <sstk-verify> widget (sk_studio_auth), which studio-api redeems at
// POST /captcha/verify BEFORE the Amplify call is made. This is ADVISORY, not
// enforcement: Amplify talks straight to Cognito from the browser, so a scripted
// client can skip this step and call Cognito itself. Enforcing it would need
// Cognito triggers, which are deliberately out of scope; the gate still stops the
// forms being driven by a naive bot. The token parameter is required so a new
// caller of these four functions cannot forget the check. Deliberately NOT
// gated: confirming the emailed code, confirming a reset code, and Google
// (signInWithRedirect), since the code or the provider is the credential there.
//
// Same default as api.ts's API_URL, not imported from it because api.ts imports
// this module.
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

/** studio-api refused, or could not reach Verify to redeem, the widget token.
 * The message is written for the form to show as it stands. */
export class VerifyError extends Error {
  readonly status: number | null;
  constructor(status: number | null) {
    super(
      status === 429
        ? 'Too many attempts. Wait a minute and try again.'
        : status === null || status >= 500
          ? 'Verification is temporarily unavailable. Please try again shortly.'
          : 'Verification failed. Please try again.',
    );
    this.name = 'VerifyError';
    this.status = status;
  }
}

async function redeemAuthCaptcha(captchaToken: string): Promise<void> {
  if (!captchaToken) throw new VerifyError(403);
  let resp: Response;
  try {
    resp = await fetch(`${API_URL}/captcha/verify`, {
      method: 'POST',
      headers: captchaHeader(captchaToken, SITE_KEY_AUTH),
      signal: AbortSignal.timeout(15_000),
    });
  } catch {
    throw new VerifyError(null);
  }
  if (!resp.ok) throw new VerifyError(resp.status);
}

export async function registerWithEmail(
  email: string,
  password: string,
  displayName: string,
  captchaToken: string,
): Promise<{ needsConfirmation: boolean }> {
  await redeemAuthCaptcha(captchaToken);
  const { isSignUpComplete } = await signUp({
    username: email,
    password,
    options: { userAttributes: { email, name: displayName } },
  });
  return { needsConfirmation: !isSignUpComplete };
}

export async function confirmRegistration(email: string, code: string): Promise<void> {
  await confirmSignUp({ username: email, confirmationCode: code });
}

export async function resendConfirmation(email: string, captchaToken: string): Promise<void> {
  await redeemAuthCaptcha(captchaToken);
  await resendSignUpCode({ username: email });
}

export async function loginWithEmail(
  email: string,
  password: string,
  captchaToken: string,
): Promise<AppUser> {
  await redeemAuthCaptcha(captchaToken);
  return signInWithPassword(email, password);
}

/**
 * The sign in that follows a successful confirmSignUp on the verify-email
 * screen. Ungated on purpose: the emailed code the user just entered is the
 * proof, and that step is not captcha-gated. Only for that hand-off; every
 * other password sign in goes through loginWithEmail.
 */
export async function loginAfterConfirmation(email: string, password: string): Promise<AppUser> {
  return signInWithPassword(email, password);
}

async function signInWithPassword(email: string, password: string): Promise<AppUser> {
  await signIn({ username: email, password });
  return toAppUser();
}

export async function loginWithGoogle(): Promise<void> {
  await signInWithRedirect({ provider: 'Google' });
}

// signInWithRedirect leaves the SPA entirely and returns on a fresh page
// load once Cognito completes the OAuth round trip, so there is no in-memory
// call site left to decide "this was a signup" once control comes back.
// sessionStorage survives that redirect in the same tab, so the Continue
// with Google button on the signup screen marks intent before leaving, and
// RedirectIfAuthed (SiteApp.tsx) reads and clears it once the user reappears
// - the same signup-vs-signin distinction the email/password flow gets for
// free from being two separate forms.
const SIGNUP_INTENT_KEY = 'ss-signup-intent';

export function markSignupIntent(): void {
  try {
    sessionStorage.setItem(SIGNUP_INTENT_KEY, '1');
  } catch {
    // Best effort - a blocked sessionStorage just means this Google sign-up
    // lands on the dashboard instead of onboarding, not a broken sign-up.
  }
}

/** True once, then clears itself - a second read after a redirect returns false. */
export function consumeSignupIntent(): boolean {
  try {
    const was = sessionStorage.getItem(SIGNUP_INTENT_KEY) === '1';
    if (was) sessionStorage.removeItem(SIGNUP_INTENT_KEY);
    return was;
  } catch {
    return false;
  }
}

export async function logout(): Promise<void> {
  await signOut();
}

export async function requestPasswordReset(email: string, captchaToken: string): Promise<void> {
  await redeemAuthCaptcha(captchaToken);
  await resetPassword({ username: email });
}

export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
}

export async function currentUser(): Promise<AppUser | null> {
  try {
    return await toAppUser();
  } catch {
    return null;
  }
}

export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export function onAuthChange(callback: (user: AppUser | null) => void): () => void {
  void currentUser().then(callback);
  return Hub.listen('auth', ({ payload }) => {
    if (payload.event === 'signedIn' || payload.event === 'tokenRefresh') {
      void currentUser().then(callback);
    } else if (payload.event === 'signedOut') {
      callback(null);
    }
  });
}
